import PropTypes from "prop-types";
import { Box, Typography } from "@material-ui/core";
import {
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Error as ErrorIcon,
} from "@material-ui/icons";

const getSlaState = (dueDate, respondedAt) => {
  if (!dueDate) return { state: "none", label: "N/A", color: "#64748B", icon: null };

  if (respondedAt) {
    const due = new Date(dueDate);
    const responded = new Date(respondedAt);
    if (responded <= due) {
      return { state: "met", label: "On Track", color: "#16A34A", icon: CheckCircleIcon };
    }
    const breachMs = responded - due;
    const hours = Math.floor(breachMs / 3600000);
    const mins = Math.floor((breachMs % 3600000) / 60000);
    const label = hours > 0 ? `Breached by ${hours}h ${mins}m` : `Breached by ${mins}m`;
    return { state: "breached", label, color: "#DC2626", icon: ErrorIcon };
  }

  const now = new Date();
  const due = new Date(dueDate);
  const remaining = due - now;

  if (remaining <= 0) {
    const breachMs = Math.abs(remaining);
    const hours = Math.floor(breachMs / 3600000);
    const mins = Math.floor((breachMs % 3600000) / 60000);
    const label = hours > 0 ? `Breached by ${hours}h ${mins}m` : `Breached by ${mins}m`;
    return { state: "breached", label, color: "#DC2626", icon: ErrorIcon };
  }

  const hoursLeft = remaining / 3600000;
  if (hoursLeft <= 1) {
    const mins = Math.floor(remaining / 60000);
    return { state: "warning", label: `${mins}m remaining`, color: "#D97706", icon: ScheduleIcon };
  }

  const hours = Math.floor(hoursLeft);
  const mins = Math.floor((remaining % 3600000) / 60000);
  if (hoursLeft <= 4) {
    return { state: "warning", label: `${hours}h ${mins}m remaining`, color: "#D97706", icon: ScheduleIcon };
  }

  return { state: "ok", label: `${hours}h ${mins}m remaining`, color: "#16A34A", icon: CheckCircleIcon };
};

const SlaIndicator = ({ dueDate, respondedAt, label, compact = false }) => {
  const sla = getSlaState(dueDate, respondedAt);
  const Icon = sla.icon;

  if (sla.state === "none") {
    return (
      <Typography variant="body2" sx={{ color: "#64748B", fontSize: 13 }}>
        N/A
      </Typography>
    );
  }

  if (compact) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
        {Icon && <Icon style={{ fontSize: 16, color: sla.color }} />}
        <Typography sx={{ fontSize: 13, fontWeight: 500, color: sla.color }}>
          {sla.label}
        </Typography>
      </Box>
    );
  }

  let bgColor;
  if (sla.state === "breached") {
    bgColor = "#FEF2F2";
  } else if (sla.state === "warning") {
    bgColor = "#FFFBEB";
  } else {
    bgColor = "#F0FDF4";
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: bgColor,
        }}
      >
        {Icon && <Icon style={{ fontSize: 22, color: sla.color }} />}
      </Box>
      <Box>
        {label && (
          <Typography sx={{ fontSize: 12, color: "#64748B" }}>{label}</Typography>
        )}
        <Typography sx={{ fontSize: 16, fontWeight: 600, color: sla.color }}>
          {sla.label}
        </Typography>
      </Box>
    </Box>
  );
};

SlaIndicator.propTypes = {
  dueDate: PropTypes.string,
  respondedAt: PropTypes.string,
  label: PropTypes.string,
  compact: PropTypes.bool,
};

export default SlaIndicator;
